import { CheckIcon, LoaderCircleIcon, PlayIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/utils'

interface TrialDemoCoverProps {
  title: string
  isStarting: boolean
  onPlay: () => void
}

export const TrialDemoCover = ({
  title,
  isStarting,
  onPlay,
}: TrialDemoCoverProps) => (
  <button
    type="button"
    onClick={onPlay}
    disabled={isStarting}
    aria-busy={isStarting}
    aria-label={`Play ${title}`}
    className="group relative size-full cursor-pointer overflow-hidden text-left focus-visible:ring-3 focus-visible:ring-brand/50 focus-visible:outline-none focus-visible:ring-inset"
  >
    <CoverBackdrop />
    <CoverScenery />
    <PlayInvitation isStarting={isStarting} />
  </button>
)

const CoverBackdrop = () => (
  <span
    aria-hidden
    className="absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_85%_10%,hsl(var(--brand)/0.35),transparent_60%),radial-gradient(ellipse_60%_60%_at_0%_100%,hsl(var(--brand-deep)/0.4),transparent_70%)]"
  />
)

const PlayInvitation = ({ isStarting }: { isStarting: boolean }) => (
  <span className="absolute inset-x-0 bottom-0 flex items-center gap-4 bg-linear-to-t from-blue-7 via-blue-7/85 to-transparent px-7 pt-16 pb-6">
    <span className="flex size-14 shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-[0_0_0_8px_hsl(var(--brand)/0.18)] transition duration-200 motion-safe:group-hover:scale-110 group-hover:brightness-110">
      {isStarting ? (
        <LoaderCircleIcon aria-hidden className="size-6 animate-spin" />
      ) : (
        <PlayIcon aria-hidden className="ml-0.5 size-6 fill-current" />
      )}
    </span>
    <span className="flex flex-col gap-1">
      <span className="text-lg font-semibold tracking-tight text-white">
        Watch a demo of CounselPro
      </span>
      <span className="text-sm text-blue-3">
        From statements to a forensic report in 4 minutes
      </span>
    </span>
  </span>
)

const CoverScenery = () => (
  <span aria-hidden className="absolute inset-0">
    <SceneryCard className="top-[9%] left-[7%] w-[46%] -rotate-3 motion-safe:group-hover:-translate-y-1.5">
      <CasePeriodCard />
    </SceneryCard>
    <SceneryCard className="top-[14%] right-[6%] w-[42%] rotate-2 motion-safe:group-hover:-translate-y-2.5">
      <ReconciledMonthCard />
    </SceneryCard>
  </span>
)

const SceneryCard = ({
  className,
  children,
}: {
  className: string
  children: ReactNode
}) => (
  <span
    className={cn(
      'absolute block rounded-lg bg-white p-4 text-blue-7 shadow-2xl shadow-black/40 transition-transform duration-500 ease-out',
      className,
    )}
  >
    {children}
  </span>
)

const CardLabel = ({ children }: { children: ReactNode }) => (
  <span className="block text-[10px] font-semibold tracking-[0.12em] text-blue-5 uppercase">
    {children}
  </span>
)

const CASE_FIGURES = [
  { label: 'Accounts', value: '4' },
  { label: 'Statements', value: '36' },
  { label: 'Pages', value: '412' },
]

const CasePeriodCard = () => (
  <span className="flex flex-col gap-3">
    <span className="flex flex-col gap-1">
      <CardLabel>Case period</CardLabel>
      <span className="text-base font-semibold tracking-tight">
        Jan 2023 – Dec 2023
      </span>
      <span className="flex items-center gap-1.5 text-xs font-medium text-brand-deep">
        <span className="size-1.5 rounded-full bg-brand" />
        92% statement coverage
      </span>
    </span>
    <span className="grid grid-cols-3 gap-2 border-t pt-3">
      {CASE_FIGURES.map((figure) => (
        <span key={figure.label} className="flex flex-col">
          <CardLabel>{figure.label}</CardLabel>
          <span className="text-sm font-semibold tabular-nums">
            {figure.value}
          </span>
        </span>
      ))}
    </span>
  </span>
)

const MONTH_LINES = [
  { label: 'Opening balance', amount: '$12,480.22' },
  { label: 'Deposits', amount: '+$8,310.00' },
  { label: 'Withdrawals', amount: '−$9,152.71' },
  { label: 'Closing balance', amount: '$11,637.51' },
]

const ReconciledMonthCard = () => (
  <span className="flex flex-col gap-2.5">
    <span className="flex items-center justify-between gap-2">
      <CardLabel>March 2023</CardLabel>
      <span className="flex items-center gap-1 rounded-full bg-brand/12 px-2 py-0.5 text-[10px] font-semibold text-brand-deep">
        <CheckIcon className="size-3" />
        Reconciled
      </span>
    </span>
    <span className="flex flex-col gap-1.5 text-xs tabular-nums">
      {MONTH_LINES.map((line) => (
        <span
          key={line.label}
          className="flex justify-between gap-3 last:border-t last:pt-1.5 last:font-semibold"
        >
          <span className="text-blue-5">{line.label}</span>
          <span>{line.amount}</span>
        </span>
      ))}
    </span>
  </span>
)
